import {
  BROKER_SYNC_ISSUE_KIND_OBJ, BROKER_SYNC_PROVIDER_LABELS, BROKER_SYNC_REVIEW_QUALITY_LABELS,
  BROKER_SYNC_RUN_STATUS_LABELS, BROKER_SYNC_RUN_STATUS_OBJ,
  type BrokerSyncAccountStatus, type BrokerSyncReviewGroup, type BrokerSyncReviewScope,
} from "@signals/shared";
import { Button } from "../_common";
import { brokerReviewDate } from "./broker-sync-issue-details";
import { formatDateTime, localeKey, t } from "./broker-sync-panel-format.util";
import { redactKnownSecrets } from "./broker-sync-panel-error.util";
import { lastSuccessfulBrokerRun } from "./broker-sync-status.hooks";

export interface AccountSyncStatusProps {
  account: BrokerSyncAccountStatus;
  onSelectScope?: ((scope: BrokerSyncReviewScope) => void) | undefined;
  secretValues?: readonly string[] | undefined;
}

function coverageText(group: BrokerSyncReviewGroup): string {
  if (group.coverage.complete) return `Состав известен: ${group.coverage.known} записей.`;
  return group.coverage.reported === null
    ? `Известный состав: ${group.coverage.known} записей; полнота истории не подтверждена.`
    : `Известный состав: ${group.coverage.known} из ${group.coverage.reported}; полный состав не подтверждён.`;
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * 24;
function stateAge(finishedAt: string | null | undefined): string {
  const finished = finishedAt ? Date.parse(finishedAt) : NaN;
  if (!Number.isFinite(finished)) return "Возраст состояния неизвестен";
  const minutes = Math.max(0, Math.floor((Date.now() - finished) / 60_000));
  return minutes < MINUTES_PER_HOUR ? `Возраст состояния: ${minutes} мин.` : minutes < MINUTES_PER_DAY
    ? `Возраст состояния: ${Math.floor(minutes / MINUTES_PER_HOUR)} ч.` : `Возраст состояния: ${Math.floor(minutes / MINUTES_PER_DAY)} дн.`;
}

export function AccountSyncStatus({ account, onSelectScope, secretValues = [] }: AccountSyncStatusProps) {
  const safe = (text: string) => redactKnownSecrets(text, secretValues);
  const run = account.latestRun;
  const successful = lastSuccessfulBrokerRun(account);
  const review = account.review;
  const status = run ? BROKER_SYNC_RUN_STATUS_LABELS[run.status][localeKey()] : t("portfolio.brokerSync.neverRun");
  const count = (label: string, value: number, scope?: BrokerSyncReviewScope) => (
    <Button type="button" size="sm" variant="outline" className="h-auto min-h-9 max-w-full whitespace-normal text-left"
      disabled={!scope || !onSelectScope} onClick={() => scope && onSelectScope?.(scope)}>{label}: {value}</Button>
  );
  const created = run?.counters["created"] ?? run?.counters["imported"];
  const outOfScope = run?.counters["outOfScope"];
  const latestScope = review?.latestRun?.scope;
  return (
    <article className="min-w-0 space-y-4 rounded-md border p-3 [overflow-wrap:anywhere]">
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-semibold">{safe(account.accountName)}</h4>
        <span className="text-sm text-muted-foreground">{BROKER_SYNC_PROVIDER_LABELS[account.provider][localeKey()]}</span>
      </div>
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>Начало загрузки: {brokerReviewDate(account.brokerSyncFrom)}</p>
        <p>Последняя успешная проверка: {successful?.finishedAt ? safe(formatDateTime(successful.finishedAt)) : successful === null ? "Ещё не завершалась" : "История неизвестна"}</p>
        <p>{stateAge(successful?.finishedAt)}</p>
      </div>

      <section className="min-w-0 space-y-2" aria-label={`Последняя проверка — ${safe(account.accountName)}`}>
        <h5 className="text-sm font-semibold">Последняя проверка</h5>
        <p className="text-sm">{status}{run ? ` · ${safe(formatDateTime(run.finishedAt ?? run.startedAt))}` : ""}</p>
        <p className="text-xs text-muted-foreground">{run?.windowFrom && run.windowTo
          ? `Период операций: ${brokerReviewDate(run.windowFrom)} — ${brokerReviewDate(run.windowTo)}`
          : latestScope?.from && latestScope.to
            ? `Период операций: ${brokerReviewDate(latestScope.from)} — ${brokerReviewDate(latestScope.to)}`
            : "Период последней проверки неизвестен"}</p>
        {run?.status === BROKER_SYNC_RUN_STATUS_OBJ.SUCCEEDED && created === 0 && <p className="text-sm">Проверка завершена, новых операций нет</p>}
        {created !== undefined && created > 0 && <p className="text-sm">Новых записей: {created}</p>}
        {!!run?.counters["updated"] && <p className="text-sm">Обновлено ранее учтённых записей: {run.counters["updated"]}</p>}
        {!!run?.counters["retryOperations"] && <p className="text-sm">Повторная обработка: {run.counters["retryOperations"]}; уже учтённые записи не добавляются повторно.</p>}
        <div className="flex min-w-0 flex-wrap gap-2">
          {review?.latestRun && count("Записи для разбора этой проверки", review.latestRun.count, latestScope)}
          {outOfScope !== undefined && outOfScope > 0 && count("Вне охвата по результату проверки", outOfScope,
            latestScope ? { ...latestScope, kind: BROKER_SYNC_ISSUE_KIND_OBJ.OUT_OF_SCOPE } : undefined)}
        </div>
        {review?.latestRun ? <p className="text-xs text-muted-foreground">{coverageText(review.latestRun)} Состав последней проверки не включает показатели качества сохранённых выплат.</p>
          : run && <p className="text-xs text-muted-foreground">Состав записей последней проверки неизвестен.</p>}
        {run?.status === BROKER_SYNC_RUN_STATUS_OBJ.FAILED && <p className="text-sm text-destructive">Проверка не завершена. Ранее полученные данные и открытые записи сохранены.</p>}
      </section>

      <section className="min-w-0 space-y-2" aria-label={`Требуют разбора — ${safe(account.accountName)}`}>
        <h5 className="text-sm font-semibold">Требуют разбора</h5>
        {count("Открытые записи", review?.open.count ?? account.openIssueCount, review?.open.scope)}
        <p className="text-xs text-muted-foreground">Все текущие открытые записи{review ? ` на ${safe(formatDateTime(review.open.scope.asOf))}` : "; время среза неизвестно"}.</p>
        {review && <p className="text-xs text-muted-foreground">{coverageText(review.open)}</p>}
        {account.pendingTransferPairCount > 0 && <p className="text-sm">Ожидают встречную часть перевода: {account.pendingTransferPairCount}. Это отдельное состояние переводов.</p>}
      </section>

      <section className="min-w-0 space-y-2" aria-label={`Исключены из загрузки — ${safe(account.accountName)}`}>
        <h5 className="text-sm font-semibold">Исключены из загрузки</h5>
        {count("Все исключённые записи", review?.ignored.count ?? account.intentionallySkippedCount, review?.ignored.scope)}
        <p className="text-xs text-muted-foreground">За период с {brokerReviewDate(review?.ignored.scope.from ?? account.brokerSyncFrom)}{review ? `; срез на ${safe(formatDateTime(review.ignored.scope.asOf))}` : "; время среза неизвестно"}. Это накопленный список, а не результат одного запуска.</p>
        {review && <p className="text-xs text-muted-foreground">{coverageText(review.ignored)}</p>}
      </section>

      {!!review?.quality.length && <section className="min-w-0 space-y-2" aria-label={`Качество учтённых выплат — ${safe(account.accountName)}`}>
        <h5 className="text-sm font-semibold">Качество учтённых выплат</h5>
        {review.quality.map((group) => <div key={group.quality} className="min-w-0 space-y-1">
          {count(BROKER_SYNC_REVIEW_QUALITY_LABELS[group.quality][localeKey()]!, group.count, group.scope)}
          <p className="text-xs text-muted-foreground">С {brokerReviewDate(group.scope.from)}; срез на {safe(formatDateTime(group.scope.asOf))}. {coverageText(group)}</p>
        </div>)}
        <p className="text-xs text-muted-foreground">Сохранённые выплаты показаны отдельно. Неизвестный налог или сумма до налога не означают, что подтверждённое зачисление потеряно.</p>
      </section>}
      <details className="min-w-0">
        <summary className="min-h-9 cursor-pointer py-2 text-sm text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Технические подробности счёта</summary>
        <p className="break-all text-xs text-muted-foreground">{t("portfolio.brokerSync.brokerAccountId")}: {safe(account.brokerAccountId)}</p>
        {run?.error && <p className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{safe(run.error)}</p>}
      </details>
    </article>
  );
}
